// Storage Keys
export const STORAGE_KEYS = {
  API_KEY: 'api-key',
  UI_STATE: 'af-monitor-ui-state',
} as const;

export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

export class LocalStorageManager {
  private static instance: LocalStorageManager;

  private constructor() {}

  static getInstance(): LocalStorageManager {
    if (!LocalStorageManager.instance) {
      LocalStorageManager.instance = new LocalStorageManager();
    }
    return LocalStorageManager.instance;
  }

  /**
   * localStorage에서 값을 가져옵니다.
   * @param key 저장소 키
   * @param defaultValue 기본값 (옵션)
   * @returns 저장된 값 또는 기본값
   */
  getItem<T>(key: StorageKey, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue || null;
      }
      return JSON.parse(item);
    } catch (error) {
      console.error(`Failed to load ${key} from localStorage:`, error);
      return defaultValue || null;
    }
  }

  /**
   * localStorage에서 문자열 값을 가져옵니다 (JSON 파싱 없이).
   * @param key 저장소 키
   * @param defaultValue 기본값 (옵션)
   * @returns 저장된 문자열 값 또는 기본값
   */
  getStringItem(key: StorageKey, defaultValue?: string): string | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue || null;
      }
      return item;
    } catch (error) {
      console.error(`Failed to load ${key} from localStorage:`, error);
      return defaultValue || null;
    }
  }

  /**
   * localStorage에 값을 저장합니다.
   * @param key 저장소 키
   * @param value 저장할 값
   */
  setItem<T>(key: StorageKey, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to save ${key} to localStorage:`, error);
    }
  }

  /**
   * localStorage에서 값을 제거합니다.
   * @param key 저장소 키
   */
  removeItem(key: StorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error);
    }
  }

  /**
   * localStorage를 완전히 비웁니다.
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * 특정 키가 localStorage에 존재하는지 확인합니다.
   * @param key 저장소 키
   * @returns 존재 여부
   */
  hasItem(key: StorageKey): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check ${key} in localStorage:`, error);
      return false;
    }
  }

  /**
   * localStorage의 모든 키를 가져옵니다.
   * @returns 키 배열
   */
  getKeys(): string[] {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Failed to get localStorage keys:', error);
      return [];
    }
  }
}

// 싱글톤 인스턴스 export
export const localStorageManager = LocalStorageManager.getInstance();
